import * as z from "zod";

/** Letters (any script), marks, Unicode dashes (hyphen, en-dash, etc.), spaces, apostrophe, period, comma */
export const PERSON_NAME_REGEX = /^[\p{L}\p{M}\p{Pd}\s'.,]+$/u;

/** Strip anything not allowed by PERSON_NAME_REGEX (for live typing in name fields) */
export const PERSON_NAME_STRIP_REGEX = /[^\p{L}\p{M}\p{Pd}\s'.,]/gu;

const nameField = (label) =>
    z
        .string()
        .min(1, `${label} is required`)
        .regex(
            PERSON_NAME_REGEX,
            `${label} may only include letters, spaces, hyphens, and ' . ,`,
        );

const passwordComplexity = z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[\W_]/, "Password must contain at least one special character");

/** US SSN display format; empty means optional (Add / Update / Edit employee). */
const SSN_FORMAT = /^\d{3}-\d{2}-\d{4}$/;

const optionalSsnField = z.preprocess(
    (val) => (typeof val === "string" ? val.trim() : ""),
    z.union([
        z.literal(""),
        z.string().regex(SSN_FORMAT, "SSN must be in format XXX-XX-XXXX"),
    ]),
);

export const getEmployeeSchema = (isUpdate = false) => {
    return z.object({
        firstName: nameField("First name"),
        lastName: nameField("Last name"),
        email: z.string().email("Invalid email format"),
        password: isUpdate
            ? z.union([z.string().length(0), passwordComplexity]).optional()
            : passwordComplexity,
        roleId: z.coerce.number().positive("Role is required"),
        status: z.enum(["Active", "Inactive"]).default("Active"),
        workMode: z.string().min(1, "Please select a work mode").refine(val => ["WFH", "WFO"].includes(val), {
            message: "Please select a work mode"
        }),
        hireDate: z.string().min(1, "Hire date is required"),
        ssn: optionalSsnField,
        mobileNumber: z.string().optional(),
        payRate: z.string().max(255, "Pay rate must be 255 characters or less").optional(),
        personalNotes: z
            .string()
            .max(2000, "Notes must be 2000 characters or less")
            .optional(),
        dateOfBirth: z.string().optional(),
        address: z.string().optional(),
        city: z.string().optional(),
        state: z.string().optional(),
        zipCode: z.string().optional(),
        country: z.string().optional(),
    });
};
